import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useToast } from '@/hooks/use-toast';

export const invoicePreviewKeys = {
  byJob: (jobId: string) => ['invoice-preview', jobId] as const,
};

export interface InvoicePreviewData {
  html: string;
  documentId: string | null;
}

export function useInvoicePreview(jobId: string | undefined) {
  return useQuery({
    queryKey: invoicePreviewKeys.byJob(jobId!),
    queryFn: async (): Promise<InvoicePreviewData> => {
      const res = await apiClient.get<Response>(`/jobs/${jobId}/invoices/preview`);
      const html = await res.text();
      const documentId = res.headers.get('X-Document-Id') || null;
      return { html, documentId };
    },
    enabled: !!jobId,
    // Drop the cached preview once the modal closes (no observers) so the next
    // open refetches fresh — without this we'd have to refetch on every toggle
    // to stay current. Regenerating the whole invoice HTML is expensive
    // (catalog lookups + full re-render); a toggle doesn't change any amounts,
    // so the iframe's local hide/show is sufficient and we skip the refetch.
    gcTime: 0,
  });
}

export function useSetLineItemItemized(jobId: string, docId: string | undefined) {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ lineItemId, itemized }: { lineItemId: string; itemized: boolean }) => {
      if (!docId) throw new Error('No active breakdown document to update');
      return apiClient.patch(
        `/jobs/${jobId}/breakdown/documents/${docId}/line-items/${lineItemId}/itemized`,
        { itemized },
      );
    },
    // Happy path: the PATCH persisted and the iframe already updated locally, so
    // there's nothing to refetch — the next modal open pulls fresh HTML (gcTime: 0).
    // On failure, refetch to roll the iframe's optimistic hide/show back to truth.
    onError: () => {
      toast({
        title: 'Could not update the invoice',
        description: 'The change was reverted. Please try again.',
        variant: 'destructive',
      });
      qc.invalidateQueries({ queryKey: invoicePreviewKeys.byJob(jobId) });
    },
  });
}
